import React, { Component, lazy, Suspense } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { withRouter } from "react-router-dom";
import "./index.scss";
import App from "./components/app";
import { ScrollContext } from "react-router-scroll-4";
import { Provider } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import store from "./store";
import FullPageLoader from "./components/searched-order/fullPageLoader";
import Login from "./components/auth/login";
import PrivateLayout from "./components/privateLayout.jsx";
import { connect } from "react-redux";
import { setCurrentAdmin } from "./actions/index.js";
import { readCachedAdmin, writeCachedAdmin } from "./utils/cachedAdmin";
import { canAccessPath } from "./utils/rolePermissions";
import { getSingleAdmin } from "./firebase/firebase.utils.js";
import {
  auth,
  createAdminProfileDocument,
  firestore,
} from "./firebase/firebase.utils";

// Route components are code-split: each page's bundle is fetched the first
// time that route is opened, instead of all of them being downloaded and
// parsed before the app can render anything. `lazy()` needs these to be
// declarations rather than imports, which is why they sit below the import
// block (the import/first lint rule requires imports to come first).
// Components
const Dashboard = lazy(() => import("./components/dashboard"));
const HandCarryMonth = lazy(() => import("./components/hand-carry/month"));
const HandCarryMonthlyOrders = lazy(
  () => import("./components/hand-carry/monthlyOrders"),
);

//  BookingRequests
const BookingList = lazy(() => import("./components/bookings/bookingList"));
const BookingRequestWarehouse = lazy(
  () => import("./components/bookings/office.js"),
);
// LotsList
const LotList = lazy(() => import("./components/lot/physical/lotlist"));
const DeliveredLot = lazy(
  () => import("./components/lot/physical/deliveredLot"),
);

//Orders
const OrdersLots = lazy(() => import("./components/sales/ordersLots"));
const OrdersD2DFreight = lazy(
  () => import("./components/sales/ordersD2DFreight"),
);
const OrdersExpressMonth = lazy(
  () => import("./components/sales/ordersExpressMonth"),
);
const OrdersExpress = lazy(() => import("./components/sales/ordersExpress"));

// Express Rates
const Document = lazy(
  () => import("./components/express-rates/document/document"),
);
const Parcel = lazy(() => import("./components/express-rates/parcel/parcel"));

// D2D Rates
const D2DRates = lazy(() => import("./components/d2d-rates/d2dRates"));

//Payments
const Payments = lazy(() => import("./components/payments/payments"));
const PaymentsExpress = lazy(
  () => import("./components/paymentsExpress/payments"),
);
const PaymentsByDate = lazy(
  () => import("./components/payments/paymentsByDate"),
);
const PaymentsByDateExpress = lazy(
  () => import("./components/paymentsExpress/paymentsByDate"),
);
const PaymentRequest = lazy(
  () => import("./components/payments/payment-request/paymentRequest"),
);
const PaymentRequestExpress = lazy(
  () =>
    import("./components/paymentsExpress/payment-request/paymentRequestExpress"),
);
// delivery
const DeliveryLots = lazy(() => import("./components/delivery/deliveryLots"));
const DeliveryD2DFreight = lazy(
  () => import("./components/delivery/deliveryD2DFreight"),
);
const DeliveryExpressMonth = lazy(
  () => import("./components/delivery/deliveryExpressMonth"),
);
const DeliveryExpress = lazy(
  () => import("./components/delivery/deliveryExpress"),
);

// refund
const RefundRequest = lazy(() => import("./components/refund/refundRequest"));
const AllRefunds = lazy(() => import("./components/refund/allRefunds"));
const AllRefundsByLots = lazy(
  () => import("./components/refund/allRefundsByLot"),
);

//Pages
const ProductToOrder = lazy(
  () => import("./components/pages/product-to-order"),
);
const Create_page = lazy(() => import("./components/pages/create-page"));
const Media = lazy(() => import("./components/media/media"));
const List_menu = lazy(() => import("./components/menus/list-menu"));
const Create_menu = lazy(() => import("./components/menus/create-menu"));
const List_user = lazy(() => import("./components/users/list-user"));
const List_admin = lazy(() => import("./components/admins/list-admin"));
const DetailAdmin = lazy(() => import("./components/admins/detail-admin"));
const DetailUser = lazy(() => import("./components/users/detail-user"));
const Attendance = lazy(() => import("./components/attendance/attendance"));
const EmployeeAttendance = lazy(
  () => import("./components/attendance/employeeAttendance"),
);

const Profile = lazy(() => import("./components/settings/profile"));
const Reports = lazy(() => import("./components/reports/report"));
const Invoice = lazy(() => import("./components/invoice/invoice"));
const InvoiceExpress = lazy(
  () => import("./components/invoice/invoiceExpress"),
);
const InvoiceExpressByMonth = lazy(
  () => import("./components/invoice/invoiceExpressByMonth"),
);
const InvoiceOrderList = lazy(
  () => import("./components/invoice/invoiceOrderList"),
);
const InvoiceByOrder = lazy(
  () => import("./components/invoice/invoice-by-order"),
);
const InvoiceByOrderExpress = lazy(
  () => import("./components/invoice/invoiceByOrderExpress"),
);
const Print = lazy(() => import("./components/invoice/print"));
const Datatable = lazy(() => import("./components/common/datatable"));
const SearchedOrder = lazy(
  () => import("./components/searched-order/searched-order"),
);

// communication
const CommunicationByLots = lazy(
  () => import("./components/communication/lots/lotList"),
);
const CommunicationByCustomers = lazy(
  () => import("./components/communication/customers/customerList"),
);
const IntroModal = lazy(
  () => import("./components/communication/introModal/introModalList"),
);
const Notice = lazy(() => import("./components/notice/notice"));
const Blogs = lazy(() => import("./components/blogs/blogs"));
const SingleBlog = lazy(() => import("./components/blogs/singleBlog"));
const AddBlog = lazy(() => import("./components/blogs/addBlog"));

// recharge
const RechargeRequest = lazy(
  () => import("./components/recharge/recharge-request/rechargeRequest"),
);
const RechargeWallet = lazy(
  () => import("./components/recharge/recharge-wallet/rechargeWallet"),
);
const RechargeHistory = lazy(
  () => import("./components/recharge/recharge-history/rechargeHistory"),
);
const RechargeHistoryByDate = lazy(
  () => import("./components/recharge/recharge-history/rechargeHistoryByDate"),
);
const PayInvoice = lazy(() => import("./components/payInvoice/payInvoice"));
const PayInvoiceExpress = lazy(
  () => import("./components/payInvoiceExpress/payInvoice"),
);
const DailyExpense = lazy(() => import("./components/expense/daily-expense"));

// expense history
const ApproveExpense = lazy(
  () => import("./components/expense-history/approve-expense/approveExpense"),
);
const Office = lazy(() => import("./components/expense-history/office/office"));
const Cnf = lazy(() => import("./components/expense-history/cnf/cnf"));
const Employee = lazy(
  () => import("./components/expense-history/employee/employee"),
);

const ExpenseByDay = lazy(
  () => import("./components/expense-history/approve-expense/daily-expense"),
);
const MonthlyExpense = lazy(
  () => import("./components/expense-history/monthly-expense/monthlyexpense"),
);
const Loans = lazy(
  () => import("./components/expense-history/loan/monthlyexpense"),
);
const LoanMonthly = lazy(
  () => import("./components/expense-history/loan/monthlyexpenseByMonth"),
);
const MonthlyExpenseByMonth = lazy(
  () =>
    import("./components/expense-history/monthly-expense/monthlyexpenseByMonth"),
);
const InvestMonth = lazy(
  () => import("./components/expense-history/invest/monthlyexpense"),
);
const Invests = lazy(
  () => import("./components/expense-history/invest/monthlyexpenseByMonth"),
);
const OfficeCostMonth = lazy(
  () => import("./components/expense-history/daily-cost/monthlyexpense"),
);
const StaffMealsMonth = lazy(
  () => import("./components/expense-history/staff-meals/monthlyexpense"),
);
const StaffMealsMonthByMonth = lazy(
  () =>
    import("./components/expense-history/staff-meals/monthlyexpenseByMonth"),
);
const LotTransportMonth = lazy(
  () => import("./components/expense-history/lot-transport/monthlyexpense"),
);
const RefundsMonth = lazy(
  () => import("./components/expense-history/refund/monthlyexpense"),
);
const CommisionMonth = lazy(
  () => import("./components/expense-history/agent-commision/monthlyexpense"),
);
const OfficeCosts = lazy(
  () => import("./components/expense-history/daily-cost/monthlyexpenseByMonth"),
);
const OfficeCostsLotTransport = lazy(
  () =>
    import("./components/expense-history/lot-transport/monthlyexpenseByMonth"),
);
const Refunds = lazy(
  () => import("./components/expense-history/refund/monthlyexpenseByMonth"),
);
const Commisions = lazy(
  () =>
    import("./components/expense-history/agent-commision/monthlyexpenseByMonth"),
);
const PartnerMonth = lazy(
  () => import("./components/expense-history/partner payment/monthlyexpense"),
);
const PartnerPayments = lazy(
  () =>
    import("./components/expense-history/partner payment/monthlyexpenseByMonth"),
);
const SalaryMonth = lazy(
  () => import("./components/expense-history/salary/monthlyexpense"),
);
const Salaries = lazy(
  () => import("./components/expense-history/salary/monthlyexpenseByMonth"),
);
const OfficeMonth = lazy(
  () => import("./components/expense-history/office/monthlyexpense"),
);
const OfficeExpenses = lazy(
  () => import("./components/expense-history/office/monthlyexpenseByMonth"),
);
const EmployeeMonth = lazy(
  () => import("./components/expense-history/employee/monthlyexpense"),
);
const EmployeeExpenses = lazy(
  () => import("./components/expense-history/employee/monthlyexpenseByMonth"),
);
const CnfMonth = lazy(
  () => import("./components/expense-history/cnf/monthlyexpense"),
);
const CnfExpenses = lazy(
  () => import("./components/expense-history/cnf/monthlyexpenseByMonth"),
);
const LcTtMonth = lazy(
  () => import("./components/expense-history/lc-tt/monthlyexpense"),
);
const LcTtByMonth = lazy(
  () => import("./components/expense-history/lc-tt/monthlyexpenseByMonth"),
);
const CashSummary = lazy(
  () => import("./components/expense-history/cash-summary/monthlyexpense"),
);
const CashSummaryByMonth = lazy(
  () =>
    import("./components/expense-history/cash-summary/monthlyexpenseByMonth"),
);
const CashSummaryByDate = lazy(
  () => import("./components/expense-history/cash-summary/daily-expense"),
);

// business consultancy
const StockProduct = lazy(
  () =>
    import("./components/business-consultancy/stock/stock-product/stock-product"),
);
const SoldMonth = lazy(
  () => import("./components/business-consultancy/stock/sold-product/month"),
);
const SoldProducts = lazy(
  () =>
    import("./components/business-consultancy/stock/sold-product/sold-product"),
);
const SourcingMonth = lazy(
  () => import("./components/business-consultancy/sourcing/invoice/month"),
);
const PurchasingMonth = lazy(
  () => import("./components/business-consultancy/purchasing/invoice/month"),
);
const Sourcing2Month = lazy(
  () => import("./components/business-consultancy/sourcing2.0/invoice/month"),
);
const SourcingMonthlyInvoice = lazy(
  () =>
    import("./components/business-consultancy/sourcing/invoice/monthly-invoices"),
);
const PurchasingMonthlyInvoice = lazy(
  () =>
    import("./components/business-consultancy/purchasing/invoice/monthly-invoices"),
);
const Sourcing2MonthlyInvoice = lazy(
  () =>
    import("./components/business-consultancy/sourcing2.0/invoice/monthly-invoices"),
);
const AgentsPurchasing = lazy(
  () =>
    import("./components/business-consultancy/purchasing/purchasing-agents/agents"),
);
const AgentsPurchasingMonths = lazy(
  () =>
    import("./components/business-consultancy/purchasing/purchasing-agents/month"),
);
const AgentsPurchasingMonthly = lazy(
  () =>
    import("./components/business-consultancy/purchasing/purchasing-agents/monthly-invoices"),
);
const NewRequest = lazy(
  () => import("./components/business-consultancy/newRequest.js"),
);
const AgentsSourcing2 = lazy(
  () =>
    import("./components/business-consultancy/sourcing2.0/sourcing-agents/agents"),
);
const AgentsSourcing2Months = lazy(
  () =>
    import("./components/business-consultancy/sourcing2.0/sourcing-agents/month"),
);
const AgentsSourcing2Monthly = lazy(
  () =>
    import("./components/business-consultancy/sourcing2.0/sourcing-agents/monthly-invoices"),
);
const AgentsSourcing = lazy(
  () =>
    import("./components/business-consultancy/sourcing/sourcing-agents/agents"),
);
const AgentsSourcingMonths = lazy(
  () =>
    import("./components/business-consultancy/sourcing/sourcing-agents/month"),
);
const AgentsSourcingMonthly = lazy(
  () =>
    import("./components/business-consultancy/sourcing/sourcing-agents/monthly-invoices"),
);
const Customers = lazy(
  () => import("./components/expense-history/loan/customer"),
);
const SingleCustomer = lazy(
  () => import("./components/expense-history/loan/singleCustomer"),
);
const CustomersInstallments = lazy(
  () => import("./components/expense-history/monthly-installment/customer"),
);
const SingleCustomerInstallments = lazy(
  () =>
    import("./components/expense-history/monthly-installment/singleCustomer"),
);
const Fund = lazy(() => import("./components/expense-history/fund/fund.js"));
const PaymentRequestSourcings = lazy(
  () =>
    import("./components/business-consultancy/payment-request/paymentRequest"),
);
const ProductDetails = lazy(
  () =>
    import("./components/business-consultancy/sourcing2.0/invoice/productDetails.js"),
);
const ProductDetailsPurchasing = lazy(
  () =>
    import("./components/business-consultancy/purchasing/invoice/productDetails.js"),
);
const FreightForwarder = lazy(
  () => import("./components/communication/freight-forwarder/list.js"),
);

// p2p
const P2pRequest = lazy(() => import("./components/p2pCustomer/p2pRequest"));
const Warehouse = lazy(() => import("./components/p2pAgent/warehouse"));
const MyShipmentRequest = lazy(
  () => import("./components/p2pAgent/myShipmentRequest"),
);
const InShipment = lazy(() => import("./components/p2pCustomer/inShipment"));
const PaymentRequestP2p = lazy(
  () => import("./components/p2pCustomer/paymentRequest"),
);
const MemberRequestP2p = lazy(
  () => import("./components/p2pAgent/memberRequest"),
);
const AllAgentsP2p = lazy(
  () => import("./components/p2pAgent/allAgentsP2p.js"),
);
const Banners = lazy(() => import("./components/communication/banner.js"));
const pnlReport = lazy(
  () => import("./components/expense-history/pnl-report/index.js"),
);

class AllRoutes extends Component {
  constructor(props) {
    super(props);
    // An admin remembered from the last session lets the shell render at once.
    // It is a head start, not a decision: the real check below still runs and
    // still overrides it. See utils/cachedAdmin.js.
    const cachedAdmin = readCachedAdmin();
    if (cachedAdmin) props.setCurrentAdmin(cachedAdmin);
    this.state = {
      authChecked: false,
      hadCachedAdmin: Boolean(cachedAdmin),
    };
  }
  unsubscribeFromAuth = null;

  // Whether the browser is sitting on the login screen *right now*. This has
  // to be read at the moment auth resolves, not captured at mount: a visit to
  // a protected URL while signed out mounts with that path, gets bounced to
  // the login screen, and would then never redirect after a successful sign-in
  // because the remembered path was never "/".
  isOnLoginRoute = () => {
    const path = this.props.location.pathname;
    return path === "/" || path === `${process.env.PUBLIC_URL}/`;
  };

  componentDidMount() {
    this.unsubscribeFromAuth = auth.onAuthStateChanged(async (adminAuth) => {
      if (adminAuth) {
        const adminRef = firestore.doc(`admins/${adminAuth.uid}`);
        const snapShot = await adminRef.get();
        if (snapShot.exists) {
          const admin = { id: snapShot.id, ...snapShot.data() };
          // Only greet on a genuine sign-in. Firing this on every reload made
          // a toast part of the page-load experience.
          if (!this.state.hadCachedAdmin) toast.success("You are logged in!");
          this.props.setCurrentAdmin(admin);
          writeCachedAdmin(admin);
          if (this.isOnLoginRoute()) {
            this.props.history.push(`${process.env.PUBLIC_URL}/dashboard`);
          }
        } else {
          toast.error("you are not an admin");
          this.props.setCurrentAdmin(null);
          writeCachedAdmin(null);
        }
      } else {
        // Authoritative: whatever the cache suggested, this session is not
        // signed in. Clearing the admin sends PrivateLayout to the login page.
        if (!this.state.hadCachedAdmin) toast.error("your are logged out!");
        this.props.setCurrentAdmin(null);
        writeCachedAdmin(null);
      }
      this.setState({ authChecked: true });
    });
  }

  componentWillUnmount() {
    if (this.unsubscribeFromAuth) {
      this.unsubscribeFromAuth(); // ✅ unsubscribe properly
    }
  }

  setCurrentAdmin = (adminObj) => {};

  render() {
    const { authChecked, hadCachedAdmin } = this.state;
    const { currentAdmin } = this.props;
    // Only hold the screen when there is genuinely nothing to show — a first
    // sign-in, or a browser with no remembered admin. With a cached admin the
    // shell and the page render straight away while the real auth check
    // completes in the background; if it comes back logged-out, currentAdmin
    // is cleared and PrivateLayout redirects to the login page.
    if (!authChecked && !hadCachedAdmin) {
      return <FullPageLoader label="Authenticating" />;
    }
    return (
      <>
        <ScrollContext>
          {/* Every route below is a `lazy()` import, so each page's code is
              fetched the first time that page is opened instead of all 134
              being downloaded and parsed before anything renders. This
              boundary catches whichever one is still in flight. */}
          <Suspense fallback={<FullPageLoader label="Loading" />}>
            <Switch>
              {/* Public  */}
              <Route
                exact
                path={`${process.env.PUBLIC_URL}/`}
                component={() => (
                  <Login setCurrentAdmin={this.setCurrentAdmin} />
                )}
              />
              {/* Protected  */}
              <Route
                path={`${process.env.PUBLIC_URL}/`}
                render={() => (
                  <PrivateLayout
                    isAuthenticated={currentAdmin !== null}
                    authResolved={authChecked}
                  >
                    {/* The shell — header, sidebar, page frame — paints
                        immediately from the remembered admin, but the page
                        itself waits for the real auth result. Without this the
                        content of a protected page flashed on screen before an
                        expired session was bounced to the login form. */}
                    {!authChecked ? (
                      <FullPageLoader
                        position="absolute"
                        label="Authenticating"
                      />
                    ) : !canAccessPath(
                        currentAdmin,
                        this.props.location.pathname,
                      ) ? (
                      // The sidebar only ever hid the links to sections a role
                      // does not have; the routes themselves were open to any
                      // signed-in admin, so an Employee could type
                      // /expense/approve-expense and approve a day of cash.
                      // One check here covers every route in the Switch below.
                      <Redirect to={`${process.env.PUBLIC_URL}/dashboard`} />
                    ) : (
                      <Switch>
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/dashboard`}
                          component={Dashboard}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/searched-order/:orderId`}
                          component={SearchedOrder}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/hand-carry`}
                          component={HandCarryMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/hand-carry/:month`}
                          component={HandCarryMonthlyOrders}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/lot/lot-list/:shipmentStatus`}
                          component={LotList}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/lot/delivered-lot`}
                          component={DeliveredLot}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/booking-request/:bookingStatus`}
                          component={BookingList}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/booking-request-warehouse`}
                          component={BookingRequestWarehouse}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/orders/express`}
                          component={OrdersExpressMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/delivery-express`}
                          component={DeliveryExpressMonth}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/calculation/express`}
                          component={(props) => (
                            <OrdersExpressMonth
                              props={props}
                              calculation={true}
                            />
                          )}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/delivery-express/:month`}
                          component={DeliveryExpress}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/order/:shipmentMethod/:shipmentStatus`}
                          component={OrdersLots}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/orders/d2d-freight/:shipmentMethodLotNo`}
                          component={OrdersD2DFreight}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/calcualation/expressOrder/:month`}
                          component={(props) => (
                            <OrdersExpress props={props} calculation={true} />
                          )}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expressOrder/:month`}
                          component={OrdersExpress}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/calculation/:shipmentMethod/:shipmentStatus`}
                          component={(props) => (
                            <OrdersLots props={props} calculation={true} />
                          )}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/calculation/d2d-freight/:shipmentMethodLotNo`}
                          component={(props) => (
                            <OrdersD2DFreight
                              props={props}
                              calculation={true}
                            />
                          )}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/invoice-d2d-freight/:shipmentMethodLotNo`}
                          component={InvoiceOrderList}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/pay-invoice/:shipmentMethodLotNo`}
                          component={PayInvoice}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/pay-invoice-express`}
                          component={PayInvoiceExpress}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/invoice-by-orderId/:orderId`}
                          component={InvoiceByOrder}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/invoice-by-bookingId/:bookingId`}
                          component={InvoiceByOrderExpress}
                        />

                        {/* express  */}

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/express-rates/document`}
                          component={Document}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/express-rates/parcel`}
                          component={Parcel}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/d2d-rates/:country`}
                          component={D2DRates}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communication/freight-forwarder`}
                          component={FreightForwarder}
                        />

                        {/* payments  */}
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/payments`}
                          component={Payments}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/payments-express`}
                          component={PaymentsExpress}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/payments-express/:date`}
                          component={PaymentsByDateExpress}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/payments/:date`}
                          component={PaymentsByDate}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/payment-request`}
                          component={PaymentRequest}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/p2p/payment-request/new`}
                          component={PaymentRequestP2p}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/p2p/member-request/new`}
                          component={MemberRequestP2p}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/p2p/member-request/agents`}
                          component={AllAgentsP2p}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/paymentRequest`}
                          component={PaymentRequestSourcings}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/payment-request-express`}
                          component={PaymentRequestExpress}
                        />

                        {/* delivery  */}
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/delivery/:shipmentMethod`}
                          component={DeliveryLots}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/delivery/d2d-freight/:shipmentMethodLotNo`}
                          component={DeliveryD2DFreight}
                        />

                        {/* refund  */}
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/refund/refund-request`}
                          component={RefundRequest}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/refund/all-refunds`}
                          component={AllRefunds}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/refund/all-refunds/:lotNo`}
                          component={AllRefundsByLots}
                        />

                        {/* communication  */}
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communication/lots`}
                          component={CommunicationByLots}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communication/customers`}
                          component={CommunicationByCustomers}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communication/notice`}
                          component={Notice}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communication/blogs`}
                          component={Blogs}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/daily-expense`}
                          component={DailyExpense}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communication/blogs/:id`}
                          component={SingleBlog}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communications/blogs/add-blog`}
                          component={AddBlog}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communication/intro-modal`}
                          component={IntroModal}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/communication/app-banner`}
                          component={Banners}
                        />

                        {/* recharge  */}
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/recharge/recharge-request`}
                          component={RechargeRequest}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/recharge/recharge-wallet`}
                          component={RechargeWallet}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/recharge/recharge-history`}
                          component={RechargeHistory}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/recharge/recharge-history/:date`}
                          component={RechargeHistoryByDate}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/pages/product-to-order`}
                          component={ProductToOrder}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/pages/create-page`}
                          component={Create_page}
                        />

                        {/* <Route exact epath={`${process.env.PUBLIC_URL}/media`} component={Media} /> */}

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/menus/list-menu`}
                          component={List_menu}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/menus/create-menu`}
                          component={Create_menu}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/users/list-user`}
                          component={List_user}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/admins/list-admin`}
                          component={List_admin}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/users/list-user/:userId`}
                          component={DetailUser}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/admins/detail-admin/:adminId`}
                          component={DetailAdmin}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/attendance`}
                          component={Attendance}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/attendance/:employeeId`}
                          component={EmployeeAttendance}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/settings/profile`}
                          component={Profile}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/invoice-d2d-freight`}
                          component={Invoice}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/pay-invoice`}
                          component={Invoice}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/invoice-express`}
                          component={InvoiceExpress}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/invoice-express/:month`}
                          component={InvoiceExpressByMonth}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/data-table`}
                          component={Datatable}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/office`}
                          component={Office}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/office/:name`}
                          component={OfficeMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/singleOffice/office/:month`}
                          component={OfficeExpenses}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/cnf`}
                          component={Cnf}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/cnf/:name`}
                          component={CnfExpenses}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expenses/cnf/:month`}
                          component={CnfMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/employee`}
                          component={Employee}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/employee/:name`}
                          component={EmployeeMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/employees/:month`}
                          component={EmployeeExpenses}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/approve-expense`}
                          component={ApproveExpense}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/pnl-report`}
                          component={pnlReport}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/expense-history`}
                          component={MonthlyExpense}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/loans`}
                          component={Loans}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/loans-by-customers`}
                          component={Customers}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/loans-by-customers/:customer`}
                          component={SingleCustomer}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/monthly-installment`}
                          component={CustomersInstallments}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/installments-by-customers/:customer`}
                          component={SingleCustomerInstallments}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/loans/:month`}
                          component={LoanMonthly}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/invest`}
                          component={InvestMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/invest/:month`}
                          component={Invests}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/salary`}
                          component={SalaryMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/salary/:month`}
                          component={Salaries}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/daily-cost`}
                          component={OfficeCostMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/staff-meals`}
                          component={StaffMealsMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/lot-transport`}
                          component={LotTransportMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/lc-tt`}
                          component={LcTtMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/refunds`}
                          component={RefundsMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/agent-commision`}
                          component={CommisionMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/fund`}
                          component={Fund}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/daily-cost/:month`}
                          component={OfficeCosts}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/staff-meals/:month`}
                          component={StaffMealsMonthByMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/lot-transport/:month`}
                          component={OfficeCostsLotTransport}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/lc-tt/:month`}
                          component={LcTtByMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/refunds/:month`}
                          component={Refunds}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/agent-commision/:month`}
                          component={Commisions}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/partner-payment/:partner`}
                          component={PartnerMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/partners/partner-payment/:month`}
                          component={PartnerPayments}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/expense-history/:month`}
                          component={MonthlyExpenseByMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/cash-summary`}
                          component={CashSummary}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/cash-summary/:month`}
                          component={CashSummaryByMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/cash-summary-by-day/:date`}
                          component={CashSummaryByDate}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/expense/approve-expense/:date`}
                          component={ExpenseByDay}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/stock`}
                          component={StockProduct}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sold`}
                          component={SoldMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/p2p/:status`}
                          component={P2pRequest}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/p2p/assigned/:status`}
                          component={InShipment}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/p2p/warehouse/:status`}
                          component={Warehouse}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/p2p/agent/:status`}
                          component={MyShipmentRequest}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sold/:month`}
                          component={SoldProducts}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing/invoice`}
                          component={SourcingMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing-invoice-months`}
                          component={Sourcing2Month}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/purchasing/invoice`}
                          component={PurchasingMonth}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing/invoice/:month`}
                          component={SourcingMonthlyInvoice}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing2/invoice/:month`}
                          component={Sourcing2MonthlyInvoice}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing2/:invoiceId`}
                          component={ProductDetails}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/purchasing2/:invoiceId`}
                          component={ProductDetailsPurchasing}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/purchasing/invoice/:month`}
                          component={PurchasingMonthlyInvoice}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/purchasing/agents`}
                          component={AgentsPurchasing}
                        />

                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing-invoice-agents`}
                          component={AgentsSourcing2}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/purchasing/agents/:agent`}
                          component={AgentsPurchasingMonths}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/purchasing/agents/:agent/:month`}
                          component={AgentsPurchasingMonthly}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcingRequest`}
                          component={NewRequest}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing/agents/:agent`}
                          component={AgentsSourcingMonths}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing/agents/:agent/:month`}
                          component={AgentsSourcingMonthly}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing2/agents/:agent`}
                          component={AgentsSourcing2Months}
                        />
                        <Route
                          exact
                          path={`${process.env.PUBLIC_URL}/business-consultancy/sourcing2/agents/:agent/:month`}
                          component={AgentsSourcing2Monthly}
                        />
                      </Switch>
                    )}
                  </PrivateLayout>
                )}
              />
            </Switch>
          </Suspense>
        </ScrollContext>
        <ToastContainer />
      </>
    );
  }
}
const mapStateToProps = (state) => ({
  currentAdmin: state.admins.currentAdmin,
});

export default withRouter(
  connect(mapStateToProps, { setCurrentAdmin })(AllRoutes),
);
